import type { ReactNode } from 'react';

/**
 * The top of a page: what this screen is, and what can be done from it.
 *
 * ⚠️ One component rather than the same three lines in every feature. The rhythm between a title,
 * its sentence and the first row of content is the thing an operator reads the page's structure
 * from, and three copies of it drift apart one spacing class at a time.
 *
 * The rule is logical spacing (`ms-`, `pe-`), never `ml-`/`pr-`: §23.10 flips the panel between
 * Arabic and English at runtime (§5.6).
 */
export function PageHeader({
  title,
  lede,
  actions,
}: {
  title: string;

  /** One sentence saying what the screen is for. Optional — a screen that needs none should not invent one. */
  lede?: string;

  /** Controls that belong to the whole page rather than to a row in it. */
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-border pb-5">
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {lede ? <p className="text-sm text-muted-foreground">{lede}</p> : null}
      </div>

      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
